/**
 * Conversation persistence for the AI Assistant.
 *
 * Threads live in localStorage under `lf-assistant-threads`, mirroring how the
 * theme (`lf-theme`) and language (`lf-lang`) persist. Deliberately hand-rolled
 * rather than using a store middleware so the payload can be versioned and a
 * refused write cannot take the page down with it.
 *
 * Three things this has to survive:
 * - **A quota error.** Long conversations add up and Safari's private mode
 *   refuses writes outright. A failed save must be a dropped save, never a
 *   thrown render.
 * - **A payload from an older build.** Anything whose `version` does not match
 *   is discarded rather than half-read, and any shape that is not the array we
 *   expect is treated as absent.
 * - **Logout.** `Header.handleLogout` calls `localStorage.clear()`, so threads
 *   go with the session. That is intended: the next user on this browser must
 *   not read the last one's conversations.
 */

const STORAGE_KEY = 'lf-assistant-threads';
const STORAGE_VERSION = 1;

/** Cap on stored history. Oldest threads fall off rather than filling quota. */
export const MAX_STORED_THREADS = 50;
/** Cap on messages kept per thread when persisting. */
export const MAX_STORED_MESSAGES = 200;

export const newId = () => {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch (error) {
    // Fall through to the timestamp form below.
  }
  return `id-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const isThread = (value) =>
  value &&
  typeof value === 'object' &&
  typeof value.id === 'string' &&
  Array.isArray(value.messages);

/** Read persisted threads. Returns [] for absent, corrupt, or stale payloads. */
export const loadThreads = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== STORAGE_VERSION) return [];
    if (!Array.isArray(parsed.threads)) return [];
    return parsed.threads.filter(isThread);
  } catch (error) {
    console.warn('Assistant: could not read saved conversations', error);
    return [];
  }
};

/** Trailing debounce for the write — see `saveThreads`. */
const SAVE_DEBOUNCE_MS = 400;
let saveTimer = null;
let pendingThreads = null;

/** Serialise and store, trimmed to `threadCap` conversations. */
const attemptWrite = (threads, threadCap) => {
  const trimmed = threads.slice(0, threadCap).map((thread) => ({
    ...thread,
    messages: (thread.messages || []).slice(-MAX_STORED_MESSAGES),
  }));
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({ version: STORAGE_VERSION, threads: trimmed }),
  );
};

const writeNow = () => {
  if (saveTimer) {
    clearTimeout(saveTimer);
    saveTimer = null;
  }
  if (!pendingThreads) return;
  const threads = pendingThreads;
  pendingThreads = null;
  try {
    attemptWrite(threads, MAX_STORED_THREADS);
  } catch (error) {
    /*
     * Almost always a quota error, and the naive handling of it is worse than
     * it looks: one oversized payload makes EVERY subsequent write fail too, so
     * the user quietly stops having any saved conversations at all — including
     * the short ones that would fit perfectly well. Halving down to the newest
     * few gives the recent conversations somewhere to land.
     *
     * The retries share the storage KEY and VERSION, so nothing here can
     * invalidate a payload an older or newer build would read.
     */
    let saved = false;
    for (let cap = Math.floor(MAX_STORED_THREADS / 2); cap >= 1 && !saved; cap = Math.floor(cap / 2)) {
      try {
        attemptWrite(threads, cap);
        saved = true;
      } catch (retryError) {
        saved = false;
      }
    }
    if (!saved) {
      // Storage is blocked outright (Safari private mode) or even one
      // conversation will not fit. In-memory still works; this must never throw.
      console.warn('Assistant: could not save conversations', error);
    }
  }
};

/**
 * Persist threads, trimming to the caps above.
 *
 * **The write is debounced, and it has to be.** Every store mutation persists,
 * and a streaming reply patches its message roughly every 60ms — so a naive
 * write would `JSON.stringify` the entire thread list about sixteen times a
 * second, for the whole length of an answer. At the caps above that is megabytes
 * of serialisation per reply, on the main thread, while the text is animating
 * in. Coalescing to one write per 400ms makes it a non-issue.
 *
 * The trade is a ≤400ms window where memory is ahead of storage, closed by the
 * `pagehide`/`visibilitychange` flush below.
 *
 * A streaming message is stored as-is; if the tab closes mid-stream the partial
 * text is what the user saw, so keeping it is more honest than dropping it.
 */
export const saveThreads = (threads) => {
  pendingThreads = threads || [];
  if (saveTimer) return;
  saveTimer = setTimeout(writeNow, SAVE_DEBOUNCE_MS);
};

/** Force any debounced write out immediately. */
export const flushThreads = () => writeNow();

if (typeof window !== 'undefined') {
  // `pagehide` fires on navigation AND on mobile tab eviction, where
  // `beforeunload` is unreliable; `visibilitychange` covers backgrounding.
  window.addEventListener('pagehide', writeNow);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') writeNow();
  });
}

export const clearStoredThreads = () => {
  // Drop any debounced write first, or it lands after the removal and puts the
  // just-deleted conversations straight back.
  if (saveTimer) {
    clearTimeout(saveTimer);
    saveTimer = null;
  }
  pendingThreads = null;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.warn('Assistant: could not clear conversations', error);
  }
};

/** Simple boolean preference, stored next to the threads. */
export const loadFlag = (key, fallback = false) => {
  try {
    const raw = localStorage.getItem(`lf-assistant-${key}`);
    if (raw === null) return fallback;
    return raw === 'true';
  } catch (error) {
    return fallback;
  }
};

export const saveFlag = (key, value) => {
  try {
    localStorage.setItem(`lf-assistant-${key}`, value ? 'true' : 'false');
  } catch (error) {
    // Non-fatal: the toggle still works for this session.
  }
};

/**
 * Title a thread from its first user message.
 *
 * Runs on the first send, so the sidebar never shows "New chat" for a
 * conversation that already has content.
 */
export const deriveTitle = (text, fallback = 'New chat') => {
  const clean = (text || '')
    .replace(/\s+/g, ' ')
    .replace(/^[#>*\-\s]+/, '')
    .trim();
  if (!clean) return fallback;
  return clean.length > 48 ? `${clean.slice(0, 48).trimEnd()}…` : clean;
};

/** Group threads into Today / Yesterday / Previous 7 days / Older buckets. */
export const groupThreadsByDate = (threads) => {
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const dayMs = 24 * 60 * 60 * 1000;

  const buckets = { today: [], yesterday: [], week: [], older: [] };
  (threads || []).forEach((thread) => {
    const stamp = new Date(thread.updatedAt || thread.createdAt || Date.now()).getTime();
    if (Number.isNaN(stamp)) {
      buckets.older.push(thread);
    } else if (stamp >= startOfToday.getTime()) {
      buckets.today.push(thread);
    } else if (stamp >= startOfToday.getTime() - dayMs) {
      buckets.yesterday.push(thread);
    } else if (stamp >= startOfToday.getTime() - 7 * dayMs) {
      buckets.week.push(thread);
    } else {
      buckets.older.push(thread);
    }
  });
  return buckets;
};
